import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider as MuiLocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { useCurrentUser, useUpdateCurrentUserTimezone } from 'src/api/user.api';
import { useAuthContext } from 'src/auth/hooks';
import { useLocales } from './use-locales';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export default function LocalizationProvider({ children }: Props) {
  const { currentLang } = useLocales();
  const { i18n } = useTranslation();

  const { isAuthenticated } = useAuthContext();
  const { data: currentUser } = useCurrentUser(isAuthenticated);
  const { mutate: reportTimezone } = useUpdateCurrentUserTimezone();

  // check user lang vs locale lang
  useEffect(() => {
    if (
      isAuthenticated &&
      currentLang.value &&
      currentUser &&
      currentUser.language &&
      currentLang.value !== currentUser.language
    ) {
      // if backend lang different than local, we use backend lang
      void i18n.changeLanguage(currentUser.language);
    }
  }, [currentLang.value, currentUser, i18n, isAuthenticated]);

  // Time zone goes the other way round from the language: the device is the
  // authority, not the stored value. Someone who flies to Montreal means a
  // different day by "tomorrow" on landing, and nobody is going to open a
  // settings screen to say so. Sent only on a real difference, so the common
  // case is zero requests.
  useEffect(() => {
    if (!isAuthenticated || !currentUser) return;
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (browserTimezone && browserTimezone !== currentUser.timezone) {
      reportTimezone({ timezone: browserTimezone });
    }
  }, [currentUser, isAuthenticated, reportTimezone]);

  return (
    <MuiLocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={currentLang.adapterLocale}
    >
      {children}
    </MuiLocalizationProvider>
  );
}
